import { ButtonReset, Stack } from '@koob/margaret';
import {
  KoobTooltip,
  KoobBadge,
  KoobButton,
  KoobConfirmationModal,
  KoobInputDateField,
  KoobInputLabel,
  KoobFormikInput,
  KoobCard,
} from '@koob/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { camelCase, isEmpty } from 'lodash';
import { FieldArray, Form, Formik, useField, useFormikContext } from 'formik';
import {
  Container,
  LeftContainer,
  RightContainer,
  SegmentedControlField,
  SplitContainer,
} from 'components/Hotel/Booking/components';
import { formatDate } from '@koob/dates';
import { formatCurrency, phoneRegex } from 'utils';
import HotelBookedSummary from './HotelBookedSummary';
import { useApp, useBooking, useSnack } from 'hooks';
import { EditorField } from 'components/Fields';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { yearsSince } from '@koob/dates';
import { SupplierEnum, TravelerKindEnum } from '@koob/enums';
import { fontStyles } from 'ui';
import LoadFromTraveler from './LoadFromTraveler';
import { submitBook } from 'api/hotels';
import { useKoobStore } from 'store';
import PhoneNumberField from 'components/Fields/PhoneNumberField';
import SelectNationatilyField from 'components/Fields/SelectNationatilyField';
import CancellationPolicies from './Edit/CancellationPolicies';
import { KoobInputCheckboxField } from '@koob/ui';
import SupplementTypes from '../../../constants/SupplementType';
import HotelBookingPoliciesWrapper from './Edit/HotelBookingPoliciesWrapper';
import { getMostRecent } from 'utils';
import {
  addGuestsToFolder,
  createDefaultCostumerFile,
} from '../../../api/folders';
import { useAsync } from 'react-async';

const Summary = styled.div`
  position: sticky;
  top: 110px;

  > * + * {
    margin-top: ${({ theme }) => theme.spacing()};
  }
`;

const FlexBlock = styled.div`
  display: flex;
  justify-content: left;
  flex-wrap: wrap;
  padding-left: 15px;
  margin-top: 15px;
  ${props =>
    Boolean(props.column) &&
    css`
      flex-direction: column;
    `};
  ${props =>
    Boolean(props.center) &&
    css`
      align-items: center;
    `};
`;

const InputContainer = styled.div`
  flex: 1;
  margin-right: ${({ theme }) => theme.spacing()};
  margin-bottom: ${({ theme }) => theme.spacing()};
`;

const FieldsContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing()};
`;

const RoomsContainer = styled.div`
  margin-top: 30px;

  > * + * {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

const TYPE_MAPPINGS = {
  adult: 'Adult',
  child: 'Child',
};

const ChildBirthdate = styled.div`
  color: ${({ theme }) => theme.textLight};
  font-size: 14px;
  padding-left: 15px;
`;

const TopDetail = styled.div`
  margin: 0;
  border-left: 3px solid #d6d6d6;
  padding-left: 15px;
`;

const RoomTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: gray;
  ${props =>
    props.condition &&
    css`
      color: #000;
    `}
`;

const ToInputContainer = styled(InputContainer)`
  display: flex;
  flex-direction: column;
`;

export const ErrorMessageWrapper = styled.div`
  ${fontStyles.bodySmall};
  color: ${({ theme }) => theme.danger};
`;

const BookingField = ({ name, children, required }) => {
  const [field, meta] = useField(name);
  const hasError = Boolean(meta.error);
  return (
    <>
      <div className="flex items-stretch gap-1">
        {children(field)}
        {required && (
          <span className="self-start text-lg text-red-600 font-bold -translate-y-2">
            *
          </span>
        )}
      </div>
      {hasError && <ErrorMessageWrapper>{meta.error}</ErrorMessageWrapper>}
    </>
  );
};

const FormField = ({ field, ...props }) => {
  const [, meta, helpers] = useField(field.name);
  return (
    <KoobFormikInput
      field={field}
      setValue={helpers.setValue}
      error={Boolean(meta.error)}
      id={field.name}
      {...props}
    />
  );
};

export const ToInfos = ({ isDisabled }) => {
  const { t } = useTranslation('booking', { keyPrefix: 'form' });

  return (
    <KoobCard style={{ width: '100%' }}>
      <div className="text-xl font-medium text-center">{t('toInfo')}</div>

      <FieldsContainer>
        <FlexBlock>
          <ToInputContainer>
            <KoobInputLabel
              htmlFor="tourOperatorName"
              label={t('tonameTitle')}
            />
            <BookingField name="tourOperatorName">
              {field => (
                <FormField
                  name="tourOperatorName"
                  type="text"
                  field={field}
                  disabled={true}
                />
              )}
            </BookingField>
          </ToInputContainer>

          <ToInputContainer>
            <KoobInputLabel htmlFor="networkName" label={t('networkName')} />
            <BookingField name="networkName">
              {field => (
                <FormField
                  name="networkName"
                  field={field}
                  type="text"
                  disabled={isDisabled}
                />
              )}
            </BookingField>
          </ToInputContainer>
        </FlexBlock>
      </FieldsContainer>
    </KoobCard>
  );
};

export const BookingOwner = ({ isDisabled, isRequired }) => {
  const { t } = useTranslation('booking', { keyPrefix: 'form' });

  return (
    <KoobCard style={{ width: '100%' }}>
      <div className="text-xl font-medium text-center">{t('bookingInfo')}</div>

      <FieldsContainer>
        <FlexBlock column>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <ToInputContainer>
              <KoobInputLabel
                htmlFor="customerFirstName"
                label={t('customerFirstNameTitle')}
                required
              />
              <BookingField name="customerFirstName">
                {field => (
                  <FormField
                    name="customerFirstName"
                    field={field}
                    type="text"
                    disabled={isDisabled}
                  />
                )}
              </BookingField>
            </ToInputContainer>

            <ToInputContainer>
              <KoobInputLabel
                htmlFor="customerLastName"
                label={t('customerLastNameTitle')}
                required
              />
              <BookingField name="customerLastName">
                {field => (
                  <FormField
                    name="customerLastName"
                    field={field}
                    type="text"
                    disabled={isDisabled}
                  />
                )}
              </BookingField>
            </ToInputContainer>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <ToInputContainer>
              <KoobInputLabel
                htmlFor="customerPhoneNumber"
                label={t('phoneNumber')}
                required={isRequired}
              />
              <BookingField name="customerPhoneNumber">
                {field => (
                  <PhoneNumberField
                    className="mt-1"
                    name="customerPhoneNumber"
                    field={field}
                    type="text"
                    disabled={isDisabled}
                  />
                )}
              </BookingField>
            </ToInputContainer>

            <ToInputContainer>
              <KoobInputLabel
                htmlFor="customerEmail"
                label={t('email')}
                required
              />
              <BookingField name="customerEmail">
                {field => (
                  <FormField
                    name="customerEmail"
                    field={field}
                    type="email"
                    disabled={isDisabled}
                  />
                )}
              </BookingField>
            </ToInputContainer>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <ToInputContainer>
              <KoobInputLabel
                htmlFor="customerCompany"
                label={t('companyTitle')}
              />
              <BookingField name="customerCompany">
                {field => (
                  <FormField
                    name="customerCompany"
                    type="text"
                    disabled={true}
                    field={field}
                  />
                )}
              </BookingField>
            </ToInputContainer>

            <ToInputContainer>
              <KoobInputLabel htmlFor="toReference" label={t('toReference')} />
              <BookingField name="toReference">
                {field => (
                  <FormField name="toReference" type="text" field={field} />
                )}
              </BookingField>
            </ToInputContainer>
          </div>
        </FlexBlock>
      </FieldsContainer>
    </KoobCard>
  );
};

export const OptionalInfoFields = ({
  name,
  disabled,
  canUpdateNationality,
}) => {
  const { t } = useTranslation('booking', { keyPrefix: 'form' });
  const [isExpanded, setIsExpanded] = useState();

  return (
    <div className="w-full rounded-2xl border border-solid border-k-gray">
      <ButtonReset
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ width: '100%' }}
      >
        <Stack size="full" alignX="space-between" padding={0.5}>
          <span>{t('optionalInfoLabel')}</span>
          <span style={isExpanded ? { transform: 'rotate(90deg)' } : undefined}>
            <i className="fa-light fa-chevron-right" style={{ fontSize: 20 }} />
          </span>
        </Stack>
      </ButtonReset>

      {isExpanded && (
        <Stack direction="column" padding={0.5} gutterSize={1} size="full">
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <BookingField name={`${name}.nationality`}>
                {field => (
                  <SelectNationatilyField
                    name={`${name}.nationality`}
                    placeholder={t('nationality')}
                    disabled={
                      canUpdateNationality !== undefined
                        ? !canUpdateNationality
                        : disabled
                    }
                    field={field}
                  />
                )}
              </BookingField>
            </div>

            <div
              style={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <BookingField name={`${name}.passportNumber`}>
                {field => (
                  <FormField
                    name={`${name}.passportNumber`}
                    type="text"
                    field={field}
                    placeholder={t('passportNumber')}
                    disabled={disabled}
                  />
                )}
              </BookingField>
            </div>

            <div
              style={{
                display: 'flex',
                flex: '0 0 fit-content',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <label htmlFor={`${name}.birthdate`}>{t('expirationDate')}</label>
              <BookingField name={`${name}.expirationDate`}>
                {field => (
                  <KoobInputDateField
                    name={`${name}.expirationDate`}
                    datePlaceholder={t('expirationDate')}
                    disabled={disabled}
                  />
                )}
              </BookingField>
            </div>
          </div>
        </Stack>
      )}
    </div>
  );
};

export const RoomPerson = ({
  type,
  index,
  birthdate,
  isRoomLeader,
  name,
  setFieldValue,
  disabled,
  customerFile,
  validateForm,
  canUpdateNationality,
  ...props
}) => {
  const { t } = useTranslation('booking');
  // TODO - Create a context to handle customerFile in the hole app instead of setup it separately for hotel booking and experience booking
  const {
    customerFile: customerFileFromContext,
    setCustomerFile,
    nationalities,
  } = useBooking();
  const [{ value }] = useField({ name });
  const { user, currentOrganizationIdInt } = useApp();
  const { values } = useFormikContext();
  const { notify } = useSnack();

  const personData = useMemo(() => {
    return name.split('.').reduce((obj, key) => {
      if (obj === undefined || obj === null) {
        return undefined;
      }

      const arrayMatch = key.match(/(\w+)\[(\d+)]/);
      if (arrayMatch) {
        const [, arrayName, index] = arrayMatch;
        return obj[arrayName]?.[parseInt(index)];
      }

      return obj[key];
    }, values);
  }, [values, name]);

  const areRequiredFieldsFilled = useMemo(() => {
    if (!personData) {
      return false;
    }

    const hasGender = personData?.gender && personData.gender.trim() !== '';
    const hasFirstName =
      personData?.firstname && personData.firstname.trim() !== '';
    const hasLastName =
      personData?.lastname && personData.lastname.trim() !== '';
    const hasBirthdate =
      personData?.birthdate != null && personData.birthdate !== '';

    return hasGender && hasFirstName && hasLastName && hasBirthdate;
  }, [personData]);

  const loadTravelerData = useCallback(
    traveler => {
      if (traveler) {
        setFieldValue(`${name}.id`, traveler.id);
        setFieldValue(`${name}.gender`, traveler.gender);
        setFieldValue(`${name}.firstname`, traveler.firstName);
        setFieldValue(`${name}.lastname`, traveler.lastName);
        setFieldValue(`${name}.birthdate`, traveler.birthdate);
        if (canUpdateNationality) {
          let nationality = undefined;
          switch (typeof traveler?.nationality) {
            case 'object':
              nationality = traveler?.nationality;
              break;
            case 'string':
              nationality = nationalities?.find(
                nationality => nationality.value === traveler?.nationality,
              );
              break;
            default:
              break;
          }
          setFieldValue(`${name}.nationality`, nationality);
        }
        setFieldValue(`${name}.passportNumber`, traveler.passportNumber);
        setFieldValue(
          `${name}.expirationDate`,
          traveler.passportExpirationDate,
        );
        if (typeof validateForm === 'function') {
          validateForm();
        }
      }
    },
    [name, setFieldValue, canUpdateNationality],
  );

  const { isPending: isCreating, run: executeCreateTraveler } = useAsync({
    deferFn: ([args]) =>
      !customerFileFromContext && !customerFile
        ? createDefaultCostumerFile(args)
        : addGuestsToFolder(args),
    onResolve: response => {
      if (!customerFileFromContext && !customerFile) {
        notify(t('success', { ns: 'folders', keyPrefix: '' }));
      } else {
        notify(t('paxAddedSuccess', { ns: 'folders', keyPrefix: '' }));
      }

      const traveler = getMostRecent(
        response.data ? response.data.travelers : response.travelers,
      );
      const newCustomerFile = response.data ? response.data : response;
      newCustomerFile.isNew = true;
      setCustomerFile(newCustomerFile);
      loadTravelerData(traveler);
    },
    onReject: error => {
      if (error?.response?.data?.message) {
        notify(error?.response?.data?.message, { type: 'error' });
      } else {
        notify(t('error', { ns: 'errors' }), { type: 'error' });
      }
    },
  });

  const handleFormatAndCreateTraveler = () => {
    const travelerObject = {
      firstName: personData?.firstname,
      lastName: personData?.lastname,
      gender: personData?.gender,
      birthdate: personData?.birthdate,
      nationality: personData?.nationality?.value,
      passportNumber: personData?.passportNumber,
      passportExpirationDate: personData?.expirationDate,
      ageIsExact: personData?.ageIsExact,
    };

    const payload =
      customerFileFromContext?.id || customerFile?.id
        ? {
            id: customerFileFromContext?.id ?? customerFile?.id,
            travelers: [travelerObject],
          }
        : {
            organizationId: currentOrganizationIdInt,
            user,
            travelers: [travelerObject],
          };
    executeCreateTraveler(payload);
  };

  return (
    <>
      <TopDetail>
        <div className="mt-5 flex justify-between items-center">
          <div className="flex space-x-1.5 items-center">
            <div className="text-xl font-semibold">
              {TYPE_MAPPINGS[type]} {index}
            </div>

            {isRoomLeader && (
              <div>
                <KoobBadge>{t('form.bookingReferee')}</KoobBadge>
              </div>
            )}

            <ChildBirthdate>
              {birthdate &&
                `${t('form.birthdate', {
                  date: formatDate(birthdate, 'DD/MM/YYYY'),
                })}`}
            </ChildBirthdate>
          </div>

          {!disabled && (
            <LoadFromTraveler
              type={type}
              customerFile={customerFileFromContext ?? customerFile}
              loadTraveler={loadTravelerData}
            />
          )}
        </div>

        <FieldsContainer>
          <BookingField name={`${name}.gender`} required>
            {field => (
              <SegmentedControlField
                name={`${name}.gender`}
                field={field}
                options={[
                  { label: t('form.male'), value: 'male' },
                  { label: t('form.female'), value: 'female' },
                  {
                    label: t('form.other'),
                    value: 'non_binary',
                  },
                ]}
                disabled={disabled}
              />
            )}
          </BookingField>
        </FieldsContainer>
      </TopDetail>

      <FieldsContainer>
        <FlexBlock center>
          <InputContainer>
            <BookingField name={`${name}.firstname`} required>
              {field => (
                <FormField
                  name={`${name}.firstname`}
                  type="text"
                  field={field}
                  placeholder={t('form.customerFirstName')}
                  disabled={disabled}
                />
              )}
            </BookingField>
          </InputContainer>

          <InputContainer>
            <BookingField name={`${name}.lastname`} required>
              {field => (
                <FormField
                  name={`${name}.lastname`}
                  type="text"
                  field={field}
                  placeholder={t('form.customerLastName')}
                  disabled={disabled}
                />
              )}
            </BookingField>
          </InputContainer>

          <InputContainer>
            <BookingField name={`${name}.birthdate`} required>
              {field => (
                <KoobInputDateField
                  name={`${name}.birthdate`}
                  datePlaceholder={t('form.birthdate')}
                  disabled={disabled}
                />
              )}
            </BookingField>
          </InputContainer>

          {['infant', 'child'].includes(type) && (
            <InputContainer className="flex space-x-1 items-center">
              <KoobInputCheckboxField
                label={
                  <span className="whitespace-nowrap">
                    {t('form.definitiveDate')}
                  </span>
                }
                name={`${name}.ageIsExact`}
                disabled={disabled}
              />

              {!value?.ageIsExact && (
                <KoobTooltip
                  tip={t('form.birthdateTip')}
                  color="purple"
                  hasArrow={false}
                  position="top"
                >
                  <i
                    className="fa-light fa-circle-question"
                    style={{ fontSize: 18 }}
                  />
                </KoobTooltip>
              )}
            </InputContainer>
          )}
        </FlexBlock>

        <OptionalInfoFields
          name={name}
          disabled={disabled}
          canUpdateNationality={canUpdateNationality}
          {...props}
        />
      </FieldsContainer>
      {!values.adminBookingId &&
        (!customerFile?.id ||
          customerFile.travelers.length === 0 ||
          customerFileFromContext?.isNew) && (
          <div className="w-full flex justify-end gap-2 mt-2">
            <KoobTooltip
              tip={
                <span className="whitespace-pre-line">
                  {t('form.savePaxTooltip')}
                </span>
              }
              position="top"
            >
              <i className="fa-solid fa-circle-info" />
            </KoobTooltip>
            <KoobButton
              type="button"
              variant="primary"
              onClick={handleFormatAndCreateTraveler}
              loading={isCreating}
              disabled={disabled || isCreating || !areRequiredFieldsFilled}
            >
              {t('form.savePax')}
            </KoobButton>
          </div>
        )}
    </>
  );
};

const BookingInfos = ({ hotel }) => {
  const {
    filters,
    selectedRooms,
    travelerRooms,
    customerFile,
    requestId,
    replaceBookingId,
    setReplaceBookingId,
    setGuests,
    setExpirationDate,
    nationalities,
  } = useBooking();
  const { t } = useTranslation('booking');
  const navigate = useNavigate();
  const refreshFolder = useKoobStore(s => s.refreshFolder);
  const { startAt, endAt } = filters;
  const { user, currentOrganization } = useApp();
  const { notify } = useSnack();
  const { hotelId } = useParams();
  const checkInDate = startAt ? new Date(startAt) : new Date();
  const [policies, setPolicies] = useState(null);
  // 409 PRICE_CHANGED_FOR_TRAVELERS: the travelers' ages changed the price.
  const [priceChange, setPriceChange] = useState(null);

  const birthdateSchema = Yup.date()
    .required(t('required', { ns: 'errors' }))
    .test('age-matches-kind', function (value) {
      if (!value) return true;
      const { kind, type } = this.parent;
      const travelerKind = kind || type;
      const age = yearsSince(value, checkInDate);
      if (travelerKind === TravelerKindEnum.ADULT && age < 18) {
        return this.createError({
          message: `This traveler will be ${age} at check-in. An adult must be at least 18.`,
        });
      }
      if (travelerKind === TravelerKindEnum.CHILD && age >= 18) {
        return this.createError({
          message: `This traveler will be ${age} at check-in. A child must be under 18.`,
        });
      }
      return true;
    });

  const ROOMS_COMPOSITIONS_SCHEMA = Yup.array()
    .of(
      Yup.object().shape({
        travelers: Yup.array()
          .of(
            Yup.object().shape({
              gender: Yup.string()
                .oneOf(['non_binary', 'male', 'female'])
                .required(t('required', { ns: 'errors' })),
              firstname: Yup.string().required(t('required', { ns: 'errors' })),
              lastname: Yup.string().required(t('required', { ns: 'errors' })),
              birthdate: birthdateSchema,
            }),
          )
          .min(1),
      }),
    )
    .min(1);

  useEffect(() => {
    setExpirationDate(selectedRooms[0].expiresAt);
  }, []);

  // `accepted` is the details of an accepted PRICE_CHANGED_FOR_TRAVELERS: the
  // API searched again for the travelers and holds the offer under a new
  // requestId and new bedCompositionIds.
  const submit = async (values, accepted) => {
    const roomsToBook = selectedRooms.reduce((acc, curr, index) => {
      const valueToAdd = {};
      if (SupplementTypes.includes(curr?.bed?.promotionInfo?.kind)) {
        valueToAdd.promotionId = curr?.bed?.promotionInfo?.promotionId;
      }
      valueToAdd.bedCompositionId =
        accepted?.rooms?.[index]?.bedCompositionId ?? curr.bed.id;
      // Own inventory publishes one offer per promotion combination, all
      // sharing the bed choice id: the promotions pick the one selected.
      if (curr?.supplier === SupplierEnum.KOOB) {
        valueToAdd.promotionIds = curr.bed?.promotionsIds;
      }
      valueToAdd.optionalPromos = curr.optionalPromos;
      valueToAdd.travelers = values.roomsToBook[index].travelers.map(v => ({
        id: v.id,
        gender: v.gender,
        firstName: v.firstname,
        lastName: v.lastname,
        nationality: v?.nationality?.value || '',
        passportNumber: v.passportNumber || '',
        kind: v.type,
        birthdate: v.birthdate,
        ageIsExact: v.ageIsExact ?? null,
        expirationDate: v.expirationDate,
      }));
      acc.push(valueToAdd);
      return acc;
    }, []);

    try {
      const data = await submitBook({
        id: hotelId,
        data: {
          ...values,
          replaceBookingId: replaceBookingId ?? undefined,
          roomsToBook,
          customerFileId: customerFile?.id,
          id: accepted?.requestId ?? requestId,
        },
      });
      if (replaceBookingId) {
        setReplaceBookingId(undefined);
        setGuests([]);
      }
      // Keep the sidebar active-folder card in sync after a booking (US-014).
      refreshFolder();
      navigate(`/bookings/${data.data}/confirmation`);
    } catch (e) {
      const { code, message: rawMessage, details } = e?.response?.data ?? {};
      if (code === 'PRICE_CHANGED_FOR_TRAVELERS') {
        setPriceChange({ values, details });
        return;
      }
      // 409 SUPPLIER_REJECTED: the wholesaler refused the booking; the API
      // message is already the user-facing dictionary text.
      if (code === 'SUPPLIER_REJECTED') {
        notify(
          t('errors:supplierRejected', {
            message: rawMessage,
            code: details?.supplierCode,
          }),
          { type: 'error' },
        );
        return;
      }
      const errorKey = camelCase(rawMessage ?? 'error');
      const translated = t(`errors:${errorKey}`);
      const hasTranslation = translated !== errorKey;
      notify(hasTranslation ? translated : rawMessage || t('errors:error'), {
        type: 'error',
      });
    }
  };
  // Formik passes its helpers as the second argument.
  const createBooking = values => submit(values);
  const priceChangeAmounts = priceChange && {
    previous: formatCurrency({
      amount: priceChange.details.previousTotalPrice,
      currency: priceChange.details.currency,
    }),
    price: formatCurrency({
      amount: priceChange.details.totalPrice,
      currency: priceChange.details.currency,
    }),
  };

  return (
    <>
      <KoobConfirmationModal
        open={!!priceChange}
        setOpen={open => !open && setPriceChange(null)}
        title={t('errors:priceChangedForTravelers.title')}
        description={t('errors:priceChangedForTravelers.description', {
          ...priceChangeAmounts,
        })}
        cancelLabel={t('errors:priceChangedForTravelers.cancel')}
        confirmLabel={t('errors:priceChangedForTravelers.confirm', {
          ...priceChangeAmounts,
        })}
        onConfirm={() => {
          const { values, details } = priceChange;
          setPriceChange(null);
          submit(values, details);
        }}
      >
        <p className="mt-3">{t('errors:priceChangedForTravelers.check')}</p>
      </KoobConfirmationModal>
      {
        <Container reservation>
          <Formik
            initialValues={{
              startAt,
              endAt,
              customerLastName: user?.lastName,
              customerFirstName: user?.firstName,
              customerCity: '',
              customerZipCode: '',
              customerAddress: '',
              customerAdditionalAddress: '',
              customerCompany: currentOrganization?.displayName,
              customerEmail: user?.email || '',
              customerPhoneNumber: user?.phoneNumber || '',
              note: '',
              toReference: filters?.toReference,
              policiesConsent: false,
              roomsToBook: (travelerRooms ?? []).map(travelerRoom => {
                return {
                  ...travelerRoom,
                  travelers: (travelerRoom.travelers ?? []).map(traveler => ({
                    ...traveler,
                    nationality: nationalities.find(
                      nationality => nationality.value === traveler.nationality,
                    ),
                  })),
                };
              }),
              tourOperatorName: currentOrganization?.displayName,
            }}
            enableReinitialize={true}
            validateOnMount={true}
            onSubmit={createBooking}
            validationSchema={Yup.object().shape({
              customerEmail: Yup.string()
                .email(t('errors:email'))
                .required(t('errors:required')),
              customerFirstName: Yup.string().required(
                t('form.customerFirstNameRequired'),
              ),
              customerLastName: Yup.string().required(
                t('form.customerLastNameRequired'),
              ),
              policiesConsent: Yup.bool().oneOf(
                [true],
                'Field must be checked',
              ),
              customerPhoneNumber: Yup.string()
                .matches(phoneRegex, t('errors:invalidFormat'))
                .when([], {
                  context: {
                    selectedRooms: selectedRooms,
                  },
                  is: () => selectedRooms[0].supplier === 'Rakuten',
                  then: Yup.string().required(t('errors:required')),
                  otherwise: Yup.string(),
                }),

              roomsToBook: ROOMS_COMPOSITIONS_SCHEMA,
            })}
          >
            {({
              isValid,
              isSubmitting,
              handleSubmit,
              setFieldValue,
              validateForm,
            }) => {
              return (
                <Form>
                  <SplitContainer reverse>
                    <LeftContainer reservation>
                      <Stack direction="column" gutterSize={1.8}>
                        <ToInfos />
                        <BookingOwner
                          isRequired={selectedRooms[0].supplier === 'Rakuten'}
                        />
                      </Stack>

                      <FieldArray name="booking">
                        {() => (
                          <RoomsContainer>
                            {travelerRooms.map((roomItem, roomIndex) => {
                              return (
                                <KoobCard
                                  key={roomIndex}
                                  style={{ width: '100%' }}
                                >
                                  <RoomTitle>
                                    {t('room.roomSelectionIndex', {
                                      roomIndex: roomIndex + 1,
                                    })}
                                  </RoomTitle>

                                  <div>
                                    {roomItem.travelers
                                      .filter(
                                        traveler => traveler.type === 'adult',
                                      )
                                      .map((tr, travelerIndex) => (
                                        <RoomPerson
                                          type={tr.type}
                                          key={`adult-${tr.id}`}
                                          name={`roomsToBook.${roomIndex}.travelers.${travelerIndex}`}
                                          index={travelerIndex + 1}
                                          isRoomLeader={
                                            roomIndex === 0 &&
                                            travelerIndex === 0
                                          }
                                          customerFile={customerFile}
                                          setFieldValue={setFieldValue}
                                          canUpdateNationality={
                                            isEmpty(
                                              roomItem.travelers[0].nationality,
                                            ) || travelerIndex !== 0
                                          }
                                          validateForm={() => validateForm()}
                                        />
                                      ))}

                                    {roomItem.travelers
                                      .filter(
                                        traveler => traveler.type === 'child',
                                      )
                                      .map((tr, travelerIndex) => {
                                        return (
                                          <RoomPerson
                                            type={tr.type}
                                            key={`child-${tr.id}`}
                                            name={`roomsToBook.${roomIndex}.travelers.${
                                              travelerIndex +
                                              roomItem.travelers.filter(
                                                traveler =>
                                                  traveler.type === 'adult',
                                              ).length
                                            }`}
                                            customerFile={customerFile}
                                            index={travelerIndex + 1}
                                            setFieldValue={setFieldValue}
                                            validateForm={() => validateForm()}
                                          />
                                        );
                                      })}
                                  </div>
                                </KoobCard>
                              );
                            })}
                          </RoomsContainer>
                        )}
                      </FieldArray>

                      <HotelBookingPoliciesWrapper
                        requestId={requestId}
                        rooms={selectedRooms}
                        setPolicies={setPolicies}
                      />

                      <CancellationPolicies
                        rooms={selectedRooms}
                        bedField="bed"
                      />
                    </LeftContainer>

                    <RightContainer reservation>
                      <Summary>
                        <HotelBookedSummary
                          startDate={filters.startAt}
                          endDate={filters.endAt}
                          hotel={hotel}
                          disabledButton={!isValid || isSubmitting}
                          rooms={selectedRooms}
                          isInfo={true}
                          roomsFilter={filters?.rooms}
                          step={2}
                          handleNextStep={handleSubmit}
                          policies={policies}
                        >
                          <Stack style={{ marginBottom: '2rem' }}>
                            <FieldsContainer style={{ flex: 1 }}>
                              <RoomTitle>{t('form.notes')}</RoomTitle>
                              <EditorField minHeight="160px" name={'note'} />
                            </FieldsContainer>
                          </Stack>
                        </HotelBookedSummary>
                      </Summary>
                    </RightContainer>
                  </SplitContainer>
                </Form>
              );
            }}
          </Formik>
        </Container>
      }
    </>
  );
};

export default BookingInfos;
